/**
 * FilterDropdown - Generic search-as-you-type multi-select dropdown
 *
 * Shared component for any grouped or flat multi-select filter.
 * Supports custom trigger icon/label, optional groups with styled headers,
 * and per-option icons. Used by KeywordFilterDropdown and other filter UIs.
 */
import type { CSSProperties, ReactElement } from 'react';
import { useMemo, useState } from 'react';

import { useClickAway } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { FaCheck, FaTimes } from 'react-icons/fa';

// ============================================================================
// Types
// ============================================================================

export interface FilterDropdownOption {
  value: string;
  label: string;
  icon?: ReactElement;
  /** Tailwind classes for the icon badge shown in the trigger (e.g. 'bg-emerald-500/20 text-emerald-400') */
  badgeClassName?: string;
  group?: string;
  style?: CSSProperties;
  /** Optional text searched instead of label (for richer matching, e.g. aliases) */
  searchText?: string;
}

export interface FilterDropdownOptionGroup {
  groupName: string;
  style?: CSSProperties;
}

export interface FilterDropdownProps {
  /** Label shown on the trigger button */
  triggerLabel: string;
  /** Optional icon rendered before the trigger label */
  triggerIcon?: ReactElement;
  /** Accessible label for the trigger button (defaults to triggerLabel) */
  ariaLabel?: string;
  /** Placeholder text inside the search input */
  searchPlaceholder?: string;
  /** Group definitions with optional header styling */
  groups?: FilterDropdownOptionGroup[];
  options: FilterDropdownOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  /** Max selected-option icons shown in trigger before collapsing to a count (default: 3) */
  maxTriggerIcons?: number;
  className?: string;
}

interface GroupWithOptions extends FilterDropdownOptionGroup {
  options: FilterDropdownOption[];
}

// ============================================================================
// Main Component
// ============================================================================

export default function FilterDropdown({
  triggerLabel,
  triggerIcon,
  ariaLabel,
  searchPlaceholder = 'Search...',
  groups,
  options,
  selectedValues,
  onToggle,
  onClear,
  maxTriggerIcons = 3,
  className,
}: FilterDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const dropdownRef = useClickAway<HTMLDivElement>(() => {
    setIsOpen(false);
    setSearch('');
  });

  const activeCount = selectedValues.length;
  const hasGroups = !!groups && groups.length > 0;

  // Resolve selected options (for trigger icon bubbles), preserving selection order
  const selectedOptions = useMemo(
    () => selectedValues.map((v) => options.find((o) => o.value === v)).filter(Boolean) as FilterDropdownOption[],
    [selectedValues, options],
  );

  // Filter options by search
  const filteredOptions = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => {
      const text = o.searchText ?? o.label;
      return text.toLowerCase().includes(q);
    });
  }, [search, options]);

  // Group filtered options by their group key
  const filteredGroups = useMemo<GroupWithOptions[] | null>(() => {
    if (!hasGroups) return null;
    return groups
      .map<GroupWithOptions>((group) => ({
        ...group,
        options: filteredOptions.filter((o) => o.group === group.groupName),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, hasGroups, filteredOptions]);

  const handleToggleOpen = () => {
    if (isOpen) {
      setSearch('');
    }
    setIsOpen(!isOpen);
  };

  const resolvedAriaLabel = ariaLabel ?? triggerLabel;

  const renderOption = (option: FilterDropdownOption) => {
    const isActive = selectedValues.includes(option.value);
    return (
      <button
        key={option.value}
        type="button"
        role="menuitemcheckbox"
        aria-checked={isActive}
        onClick={() => onToggle(option.value)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-zinc-700/50"
      >
        <span
          className={clsx(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
            isActive ? 'border-aether bg-aether text-zinc-900' : 'border-zinc-600 bg-transparent',
          )}
        >
          {isActive && <FaCheck className="h-2.5 w-2.5" />}
        </span>
        {option.icon && <span className="shrink-0">{option.icon}</span>}
        <span className="text-zinc-200" style={option.style}>
          {option.label}
        </span>
      </button>
    );
  };

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggleOpen}
        className={clsx(
          'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
          activeCount > 0
            ? 'bg-aether/20 text-aether'
            : isOpen
              ? 'bg-zinc-700 text-zinc-100'
              : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${resolvedAriaLabel}${activeCount > 0 ? ` (${activeCount} active)` : ''}`}
      >
        {triggerIcon}
        <span>{triggerLabel}</span>
        {activeCount > 0 && (
          <span className="ml-1 flex items-center gap-0.5">
            {selectedOptions.slice(0, maxTriggerIcons).map((opt) =>
              opt.icon ? (
                <span
                  key={opt.value}
                  className={clsx('inline-flex h-5 w-5 items-center justify-center rounded-full', opt.badgeClassName)}
                  title={opt.label}
                >
                  {opt.icon}
                </span>
              ) : (
                <span
                  key={opt.value}
                  className={clsx(
                    'inline-flex h-5 items-center justify-center rounded-full px-1.5 text-[10px] font-medium',
                    opt.badgeClassName || 'bg-aether text-zinc-900',
                  )}
                  title={opt.label}
                >
                  {opt.label}
                </span>
              ),
            )}
            {activeCount > maxTriggerIcons && (
              <span className="bg-aether flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold text-zinc-900">
                +{activeCount - maxTriggerIcons}
              </span>
            )}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute top-full left-0 z-50 mt-1 w-72 rounded-lg border border-zinc-700 bg-zinc-800 shadow-xl" role="menu">
          {/* Search input */}
          <div className="border-b border-zinc-700 p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="focus:border-aether w-full rounded-md border border-zinc-600 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-500 focus:outline-none"
              autoFocus
              aria-label={searchPlaceholder}
            />
          </div>

          {/* Scrollable option list */}
          <div className="max-h-72 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-zinc-500">No options match</div>
            ) : filteredGroups ? (
              /* Grouped rendering */
              filteredGroups.map((group, groupIdx) => (
                <div key={group.groupName}>
                  {groupIdx > 0 && <div className="my-1 border-t border-zinc-700" />}
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider uppercase" style={group.style}>
                    {group.groupName}
                  </div>
                  {group.options.map(renderOption)}
                </div>
              ))
            ) : (
              /* Flat rendering (no groups) */
              filteredOptions.map(renderOption)
            )}
          </div>

          {/* Clear button */}
          {activeCount > 0 && (
            <>
              <div className="border-t border-zinc-700" />
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setSearch('');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300"
              >
                <FaTimes className="h-3 w-3" />
                <span>Clear Filter</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
